import { useMemo, useState } from 'react';
import { useAdminI18n } from '../../../lib/i18n/use-admin-i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { useAuthStore } from '../../../stores/authStore';
import { DynamicTable } from '../../../components/custom/table/DynamicTable';
import type {
  DynamicTableColumnConfig,
  DynamicTableControllers,
} from '../../../components/custom/table/dynamic-table-types';
import { saasApi, type TenantSubscriptionWithRelations } from '../../../lib/api/saas.api';
import { AssignPlanModal } from './AssignPlanModal';
import {
  AdminTableRowActions,
  PagePrimaryActionButton,
} from '../../../components/common/AdminTableRowActions';
import { TenantSubscriptionDetailModal } from './TenantSubscriptionDetailModal';

interface TenantSubscriptionsTableProps {
  readonly rows: TenantSubscriptionWithRelations[];
  readonly controllers?: DynamicTableControllers;
  readonly rowOffset: number;
  readonly onView: (row: TenantSubscriptionWithRelations) => void;
  readonly onDeactivate: (row: TenantSubscriptionWithRelations) => void;
  readonly isDeactivating: boolean;
  readonly isLoading: boolean;
}

function TenantSubscriptionsTable({
  rows,
  controllers,
  rowOffset,
  onView,
  onDeactivate,
  isDeactivating,
  isLoading,
}: TenantSubscriptionsTableProps) {
  const { col, act, page, tableLoading } = useAdminI18n();

  const columns: DynamicTableColumnConfig<TenantSubscriptionWithRelations>[] = useMemo(
    () => [
    {
      id: 'no',
      header: col('no'),
      columnType: 'number',
      cell: ({ index }) => rowOffset + index + 1,
      widthMode: 'fit',
      size: 48,
      meta: { className: 'text-muted-foreground' },
    },
    {
      id: 'tenantId',
      header: col('tenant'),
      accessorKey: 'tenantId',
      columnType: 'text',
      sortable: true,
      cell: ({ row }) =>
        row.tenant?.name ?? row.tenant?.code ?? String(row.tenantId).slice(0, 8) + '…',
    },
    {
      id: 'planId',
      header: col('plan'),
      accessorKey: 'planId',
      columnType: 'text',
      sortable: true,
      cell: ({ row }) =>
        row.plan?.name ?? row.plan?.code ?? String(row.planId).slice(0, 8) + '…',
    },
    {
      id: 'startsAt',
      accessorKey: 'startsAt',
      header: col('starts'),
      sortable: true,
      columnType: 'text',
      cell: ({ value }) =>
        value ? new Date(value as string).toLocaleDateString(undefined, { dateStyle: 'short' }) : '—',
    },
    {
      id: 'endsAt',
      accessorKey: 'endsAt',
      header: col('ends'),
      sortable: true,
      columnType: 'text',
      cell: ({ value }) =>
        value ? new Date(value as string).toLocaleDateString(undefined, { dateStyle: 'short' }) : '—',
    },
    {
      id: 'isActive',
      accessorKey: 'isActive',
      header: col('active'),
      columnType: 'text',
      cell: ({ value }) => (value ? 'Yes' : 'No'),
      widthMode: 'fit',
      size: 80,
    },
    {
      id: 'actions',
      header: col('actions'),
      columnType: 'actions',
      cell: ({ row }) => (
        <AdminTableRowActions
          act={act}
          onView={() => onView(row)}
          onDeactivate={row.isActive ? () => onDeactivate(row) : undefined}
          deactivateDisabled={isDeactivating}
        />
      ),
      widthMode: 'fit',
      size: 180,
      minSize: 140,
      meta: { className: 'min-w-[140px] text-right' },
    },
    ],
    [act, col, isDeactivating, onDeactivate, onView, rowOffset],
  );

  return (
    <DynamicTable<TenantSubscriptionWithRelations>
      features={{
        columns: true,
        rows: true,
        sorting: true,
        pagination: true,
        globalFilter: false,
        stripedRows: true,
        fontSize: 'xs',
        compactPadding: true,
      }}
      columns={columns}
      rows={{
        data: rows,
        getRowId: row => row.id as string,
      }}
      controllers={controllers}
      isLoading={isLoading}
      loadingMessage={tableLoading}
      emptyMessage={page('subscriptions', 'empty')}
    />
  );
}

export function SubscriptionsPage() {
  const { page } = useAdminI18n();
  const tenantId = useAuthStore(s => s.tenantId ?? null);
  const queryClient = useQueryClient();
  const [subscriptionSorting, setSubscriptionSorting] = useState<SortingState>([
    { id: 'startsAt', desc: true },
  ]);
  const [subscriptionPagination, setSubscriptionPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [assignPlanOpen, setAssignPlanOpen] = useState(false);
  const [subscriptionDetail, setSubscriptionDetail] = useState<TenantSubscriptionWithRelations | null>(null);

  const { data: tenantSubscriptions = [], isLoading: subscriptionsLoading } = useQuery({
    queryKey: ['tenant-subscriptions', tenantId ?? ''],
    queryFn: () => saasApi.getTenantSubscriptions(tenantId ?? undefined),
  });

  const sortedSubscriptions = useMemo(() => {
    if (!subscriptionSorting.length) return tenantSubscriptions;
    const first = subscriptionSorting[0];
    if (!first) return tenantSubscriptions;
    const { id, desc } = first;
    const field = id as keyof TenantSubscriptionWithRelations;
    return [...tenantSubscriptions].sort((a, b) => {
      const av = (a as Record<string, unknown>)[field];
      const bv = (b as Record<string, unknown>)[field];
      if (av == null && bv == null) return 0;
      if (av == null) return desc ? -1 : 1;
      if (bv == null) return desc ? 1 : -1;
      const as =
        typeof av === 'object' && av !== null && typeof (av as Date).getTime !== 'function'
          ? JSON.stringify(av)
          : String(av);
      const bs =
        typeof bv === 'object' && bv !== null && typeof (bv as Date).getTime !== 'function'
          ? JSON.stringify(bv)
          : String(bv);
      const cmp = as.localeCompare(bs, undefined, { numeric: true });
      return desc ? -cmp : cmp;
    });
  }, [tenantSubscriptions, subscriptionSorting]);

  const subscriptionTotal = sortedSubscriptions.length;
  const subscriptionPageSize = subscriptionPagination.pageSize;
  const subscriptionPageCount = Math.max(
    1,
    Math.ceil(Math.max(1, subscriptionTotal) / subscriptionPageSize),
  );
  const subscriptionSafePageIndex = Math.min(
    subscriptionPagination.pageIndex,
    subscriptionPageCount - 1,
  );
  const subscriptionPageState: PaginationState = {
    pageIndex: subscriptionSafePageIndex,
    pageSize: subscriptionPageSize,
  };
  const paginatedSubscriptions = useMemo(() => {
    const start = subscriptionSafePageIndex * subscriptionPageSize;
    return sortedSubscriptions.slice(start, start + subscriptionPageSize);
  }, [sortedSubscriptions, subscriptionSafePageIndex, subscriptionPageSize]);

  const deactivateMutation = useMutation({
    mutationFn: (id: string) =>
      saasApi.updateTenantSubscription(id, {
        isActive: false,
        endsAt: new Date().toISOString(),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-subscriptions'] });
    },
  });

  const handleDeactivate = async (row: TenantSubscriptionWithRelations) => {
    if (
      !globalThis.confirm(
        `Deactivate subscription for tenant "${row.tenant?.name ?? row.tenantId}"?`,
      )
    ) {
      return;
    }
    try {
      await deactivateMutation.mutateAsync(String(row.id));
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to deactivate tenant subscription';
      alert(message);
    }
  };

  const subscriptionControllers: DynamicTableControllers = {
    sort: {
      state: subscriptionSorting,
      onSortingChange: updater => {
        setSubscriptionSorting(prev =>
          typeof updater === 'function' ? updater(prev) : updater,
        );
        setSubscriptionPagination(prev => ({ ...prev, pageIndex: 0 }));
      },
    },
    pagination: {
      state: subscriptionPageState,
      pageCount: subscriptionPageCount,
      rowCount: subscriptionTotal,
      onPaginationChange: updater => {
        setSubscriptionPagination(prev =>
          typeof updater === 'function' ? updater(prev) : updater,
        );
      },
    },
  };

  return (
    <div className="space-y-6 p-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">{page('subscriptions', 'title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{page('subscriptions', 'subtitle')}</p>
        </div>
        <PagePrimaryActionButton onClick={() => setAssignPlanOpen(true)}>
          {page('subscriptions', 'new')}
        </PagePrimaryActionButton>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="p-2">
          <TenantSubscriptionsTable
            rows={paginatedSubscriptions}
            controllers={subscriptionControllers}
            rowOffset={subscriptionPageState.pageIndex * subscriptionPageState.pageSize}
            onView={row => setSubscriptionDetail(row)}
            onDeactivate={handleDeactivate}
            isDeactivating={deactivateMutation.isPending}
            isLoading={subscriptionsLoading}
          />
        </div>
      </div>

      <TenantSubscriptionDetailModal
        open={subscriptionDetail !== null}
        onClose={() => setSubscriptionDetail(null)}
        subscription={subscriptionDetail}
      />

      <AssignPlanModal
        open={assignPlanOpen}
        onClose={() => setAssignPlanOpen(false)}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['tenant-subscriptions'] })}
      />
    </div>
  );
}
